import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TruncatedText } from "@/components/common/truncated-text";
import { IconButton } from "@/components/common/icon-button";
import { AudioPlayButton } from "@/components/common/audio-play-button";
import { TextEditable } from "@/components/common/text-editable";
import { EditIcon } from "@/components/agent-dashboard/assets";
import { DeleteConfirmModal } from "@/components/ui/modal";
import { TrashIcon } from "@/components/ui/icon";
import { Check } from "lucide-react";
import { formatTimeByYear } from "@/utils/date-formatter";
import { MinusCircleIcon } from "../agent-creation/assets/minus-circle-icon";
import { CheckboxCheckedIcon } from "../assets-knowledge-item/assets";
import { CheckboxIcon } from "./assets/checkbox-icon";

interface VoiceCardProps {
  id: string;
  name: string;
  audioUrl: string;
  createdAt?: string;

  width?: string;
  height?: string;
  showEdit?: boolean;
  /** 是否启用选择 */
  enableSelect?: boolean;
  /** 是否被选中 */
  isSelected?: boolean;
  /** 是否已添加 */
  added?: boolean;
  className?: string;
  onRename?: (name: string) => void;
  onSelect?: (id: string, selected: boolean) => void;
}

/** 配音卡片  */
export const VoiceCard = ({
  name,
  audioUrl,
  id,
  createdAt,
  width,
  height,
  showEdit,
  enableSelect,
  isSelected = false,
  added = false,
  className,
  onRename,
  onSelect,
}: VoiceCardProps) => {
  const { t } = useTranslation();
  // 为每个卡片添加独立的编辑状态
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState(isSelected);

  // 当父组件传入的isSelected变化时，更新本地selected状态
  useEffect(() => {
    setSelected(isSelected);
  }, [isSelected]);

  const handleRename = (name: string) => {
    if (!onRename) return;
    setIsEditing(false);
    onRename?.(name);
  };

  const handleSelect = (id: string) => {
    if (added) return;

    const newSelected = !selected;
    setSelected(newSelected);
    onSelect?.(id, newSelected);
  };

  return (
    <div
      className={cn(
        "group relative h-[76px] w-full max-w-full flex items-center gap-4 p-5 rounded-xl border border-[#eaecf0] bg-white hover:border-[#667085] transition-border-color overflow-hidden",
        className,
        enableSelect && "cursor-pointer",
        selected && "border-black hover:border-black",
      )}
      style={{
        width,
        height,
      }}
      onClick={() => enableSelect && handleSelect(id)}
    >
      <AudioPlayButton
        key={id}
        iconClassName="w-9 h-9"
        disabled={!audioUrl}
        url={audioUrl}
      />
      <div className="flex flex-col gap-1 w-full relative min-w-0 ">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center min-w-0">
            <TextEditable
              className="text-base font-medium pl-0 max-w-[85%] "
              value={name}
              onChange={(name) => handleRename(name)}
              isEditing={isEditing}
              onStartEditing={() => onRename && setIsEditing(true)}
              onEndEditing={() => onRename && setIsEditing(false)}
              mode="doubleClick"
            />
            {showEdit && (
              <IconButton
                icon={<EditIcon className="size-4 cursor-pointer" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              />
            )}
          </div>
          <TruncatedText text={formatTimeByYear(createdAt)} className="text-xs text-[rgba(71,84,103,1)] mb-1" />
        </div>
      </div>
      {enableSelect && !added && (
        <div
          className={cn(
            "absolute top-3 right-3 flex items-center justify-center transition-all",
          )}
        >
          {selected ? (
            <CheckboxCheckedIcon />
          ) : (
            <CheckboxIcon />
          )}
        </div>
      )}
      {added && (
        <span className="absolute top-3 right-3 text-xs bg-[rgba(242,244,247,1)] text-[rgba(16,24,40,1)] py-1 px-2 rounded-[6px]" >
          已添加
        </span>
      )}
    </div>
  );
};
